import type { Ref } from 'vue';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import transform from 'lodash-es/transform';
import isObject from 'lodash-es/isObject';
import trimEnd from 'lodash-es/trimEnd';
import cloneDeep from 'lodash-es/cloneDeep';
import { DateTime } from 'luxon';

export async function fileDownload(url: string, filename: string, params?: any)
{
    return axios
        .get(url, {
            params: { ...params },
            responseType: 'blob'
        })
        .then((res) =>
        {
            if (res.status == 200)
            {
                const blob = res.data;

                if ((window.navigator as any).msSaveOrOpenBlob)
                {
                    (navigator as any).msSaveBlob(blob, filename);
                }
                else
                {
                    const a = document.createElement('a');

                    document.body.appendChild(a);
                    a.href = window.URL.createObjectURL(blob);
                    a.download = filename;
                    a.target = '_blank';
                    a.click();
                    a.remove();
                }
            }
        });
}

export function blobDownload(blob: Blob, filename: string)
{
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
}

export function formatFileSize(size: number)
{
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const threshold = 1024;

    size = Number(size) * threshold;

    const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(threshold));

    size = size / Math.pow(threshold, i);

    return `${size.toFixed(2)} ${units[i]}`;
}

export function formatLocaleNumber(value: number | null | undefined, options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }, locale: string = 'pl-PL')
{
    if (value === null || value === undefined || Number.isNaN(Number(value)))
    {
        return '';
    }

    const minimumFractionDigits = options?.minimumFractionDigits ?? 0;
    const maximumFractionDigits = options?.maximumFractionDigits ?? minimumFractionDigits;

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits,
        maximumFractionDigits,
        // pl-PL domyślnie grupuje dopiero od 10 000 — wymuszamy separator tysięcy już od 1 000
        useGrouping: 'always'
    }).format(Number(value));
}

export function formatLocalePrice(value: number | null | undefined, currencySymbol?: string, locale: string = 'pl-PL')
{
    const formatted = formatLocaleNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, locale);

    if (!formatted)
    {
        return '';
    }

    if (!currencySymbol)
    {
        return formatted;
    }

    return `${formatted} ${currencySymbol.trim()}`;
}

export function polishPlurals(singularNominativ: string, pluralNominativ: string, pluralGenitive: string, value: number)
{
    value = Math.abs(value);

    if (value === 1)
        return singularNominativ;
    else if (value % 10 >= 2 && value % 10 <= 4 && (value % 100 < 10 || value % 100 >= 20))
        return pluralNominativ;
    else
        return pluralGenitive;
}

export function deepMap(data: any, callback: (data: any) => any, policy: (data: any) => boolean = (value: any) => true)
{
    return transform(data, (result: any, value: any, key: string | number) =>
    {
        result[key] = policy(value) && isObject(value) ? deepMap(value, callback, policy) : callback(value);

        return result;
    });
}

export function formatDateTime(input: DateTime | Date | string, format: string = 'yyyy-MM-dd HH:mm:ss', zone: string = null, defaultValue: string = '-')
{
    if (input && format)
    {
        let value = cloneDeep(input);
        const zoneName = zone || DateTime.local().zoneName;

        if (value instanceof Date)
            value = DateTime.fromJSDate(value);

        if (typeof value === 'string')
            value = DateTime.fromISO(value);

        if (value instanceof DateTime)
            return value.setZone(zoneName).toFormat(format);
    }

    return defaultValue;
}

export function baseurl(value: string = '')
{
    const config = useRuntimeConfig();

    return `${trimEnd(config.public.appUrl, '/')}/${value}`;
}

export function getApiUrl()
{
    const config = useRuntimeConfig();

    return `${trimEnd(config.public.appUrl, '/')}/api`;
}

export function getAiUrl()
{
    const config = useRuntimeConfig();
    const aiUrl = trimEnd(config.public.aiUrl, '/');

    return `${aiUrl || window.location.origin}/api`;
}

export function getAppOrigin()
{
    const config = useRuntimeConfig();
    const appUrl = trimEnd(config.public.appUrl, '/');

    // dla adresów konsumowanych poza SPA (muszą być bezwzględne) — przy pustym appUrl backend stoi za originem panelu
    return appUrl || window.location.origin;
}

const ISO_8601 = /(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2}(\.\d{3})?)Z/;

export function convertDates(result: any)
{
    if (result && isObject(result))
    {
        result = deepMap(result, function (value)
        {
            if (typeof value === 'string' && (value.length == 20 || value.length == 24) && ISO_8601.test(value))

                return DateTime.fromISO(value).setZone('UTC');

            return value;
        });
    }

    return result;
}

export function datesToString(result: any)
{
    if (result && isObject(result))
    {
        result = deepMap(result,
            (value) =>
            {
                if (DateTime.isDateTime(value))

                    return value.toString();

                return value;
            },
            (value) =>
            {
                return !DateTime.isDateTime(value);
            }
        );
    }

    return result;
}

export async function wait(valueFactory: () => any)
{
    return await new Promise((resolve, reject) =>
    {
        const internal = () =>
        {
            const value = valueFactory();

            if (value)
                return resolve(value);

            setTimeout(internal, 500);
        };

        internal();
    });
}

export function randomNumber(min: number = 1, max: number = 10000000)
{
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function sleep(ms: number = 1000)
{
    return new Promise((resolve) =>
    {
        setInterval(() => resolve(), ms);
    });
}

export function dump(...params: any[])
{
    // eslint-disable-next-line no-console
    console.log('DUMP', ...params.map(p => JSON.parse(JSON.stringify(p))));
}

export function only(item: any, ...properties: string[])
{
    const result = {} as any;

    Object.keys(item).forEach((key) =>
    {
        if (properties.includes(key))

            result[key] = item[key];
    });

    return result;
}

export function except(item: any, ...properties: string[])
{
    const result = {} as any;

    Object.keys(item).forEach((key) =>
    {
        if (!properties.includes(key))

            result[key] = item[key];
    });

    return result;
}

type ClassValue = string | false | null | undefined | ClassValue[] | Record<string, boolean>;

export function normalizeClasses(classes: ClassValue): Record<string, boolean>
{
    const result: Record<string, boolean> = {};

    function append(value: ClassValue)
    {
        if (!value)
            return;

        if (Array.isArray(value))
        {
            value.forEach(item => append(item));

            return;
        }

        if (typeof value === 'object')
        {
            Object.entries(value).forEach(([name, enabled]) =>
            {
                if (enabled)
                    append(name);
            });

            return;
        }

        value.split(/\s+/).forEach((name) =>
        {
            if (name.length > 0)
                result[name] = true;
        });
    }

    append(classes);

    return result;
}

export function handleException(log: Logger, ex: any, rules: Record<number, (ex: any) => void> = {}, events: any = null)
{
    const { $events } = useEvents();
    const errors = Object.values(ex?.data?.errors ?? {}).flat();

    if (errors.length > 0)
        $events?.$emit('error-occured');

    const code = Number(ex.code || 0);
    const codes = Object.keys(rules).map(key => Number(key));
    const matched = codes.indexOf(code) >= 0 || codes.indexOf(0) >= 0;

    if (codes.indexOf(code) >= 0)
        rules[code](ex);
    else if (codes.indexOf(0) >= 0)
        rules[0](ex);

    if (!ex.code || ex.code >= 500 || !matched)
        log.debug(ex);

    // Kod spoza niepustej mapy zostaje nieobsłużony, żeby przechwytywacz z `plugins/http.ts`
    // pokazał swój komunikat (403, 404) zamiast ciszy
    if ('handled' in ex && (matched || codes.length == 0))
    {
        ex.handled = true;
    }
}

export function useMouseClick()
{
    let mouseClicks = 0;
    let mouseTimer: any = null;

    return (click: () => void, dbClick: () => void) =>
    {
        mouseClicks += 1;

        if (mouseClicks > 2)
            mouseClicks = 1;

        if (mouseClicks == 1)
        {
            if (mouseTimer)
                clearTimeout(mouseTimer);

            mouseTimer = setTimeout(() =>
            {
                if (mouseClicks == 1)
                    click();
                else
                    dbClick();

                mouseClicks = 0;
            },
            250);
        }
    };
}

interface Shortcuts
{
    ctrlA?: Function;
}

export function useShortcuts(options: Shortcuts = {})
{
    const onKeyUp = (ev: KeyboardEvent) =>
    {
        switch (ev.code)
        {
            case 'KeyA':
            {
                if (ev.ctrlKey && options.ctrlA)
                {
                    ev.preventDefault();
                    options.ctrlA?.();
                }

                break;
            }
        }
    };

    onMounted(() =>
    {
        window.addEventListener('keydown', onKeyUp);
    });

    onBeforeUnmount(() =>
    {
        window.removeEventListener('keydown', onKeyUp);
    });
}

export function useShiftSelect()
{
    const cursor = ref(0);
    const cursorDestination = ref(-1);

    const onClick = (checkedItems: number[], ids: number[], id: number, e: PointerEvent) =>
    {
        if (e.shiftKey)
        {
            const index = ids.indexOf(id);

            const toRemove = cursor.value === cursorDestination.value
                ? []
                : ids.slice(
                    Math.min(cursor.value + 1, cursorDestination.value),
                    Math.max(cursor.value, cursorDestination.value + 1)
                );
            const toAdd = ids.slice(Math.min(cursor.value + 1, index), Math.max(cursor.value, index + 1));

            if (toAdd.includes(id) && checkedItems.includes(id))
                e.preventDefault();

            const arr = [
                ids[cursor.value],
                ...checkedItems.filter(el => !toRemove.includes(el)),
                ...toAdd
            ];

            cursorDestination.value = index;

            return Array.from(new Set(arr));
        }
        else
        {
            cursor.value = ids.indexOf(id);
            cursorDestination.value = -1;

            return checkedItems;
        }
    };

    return { cursor, cursorDestination, onClick };
}

export function abort(code: number, message: string)
{
    throw (() =>
    {
        return {
            code: code,
            message: message,
            data: null as any,
            inner: null as any
        };
    })();
}

export function slugify(value: string)
{
    const search = [' ', 'ę', 'ó', 'ą', 'ś', 'ł', 'ż', 'ź', 'ć', 'ń'];
    const replace = ['-', 'e', 'o', 'a', 's', 'l', 'z', 'z', 'c', 'n'];
    let text = value.toLowerCase();

    for (let i = 0; i < search.length; i++)
    {
        const re = new RegExp(search[i], 'g');

        text = text.replace(re, replace[i]);
    }

    return text.replace(/[^a-z0-9-]/g, '');
}

export function isObjectEmpty(value: any)
{
    return value == null || (Object.keys(value).length === 0 && value.constructor === Object);
}
